/**
 * Line-of-sight over a {@link Space} — pure, render-free, rules-free, the LoS
 * sibling of {@link ./path movement}. **Generic over the grid** (takes a
 * `Space<P>`, walking `space.line`), so the identical sightline logic runs on
 * hex and square alike. Like `path.ts`, it stays free of any map representation:
 * what blocks a sightline enters through closures the rules/encounter layer
 * supplies (`blocksCell` for solid cells, `blocksEdge` for thin walls), exactly
 * as `enterCost` carries blockers into movement.
 *
 * Two layers:
 *  - {@link sight} — the **single-line primitive**. Walks one `line(a, b)` spine;
 *    reports whether it's clear.
 *  - {@link visibility} — the **multi-line, footprint-folding** query. Units occupy
 *    multi-cell Size footprints, so RAW cover is "any part of *your* space to any
 *    part of *theirs*". `visibility` runs `sight` across the cross-product of two
 *    footprints (the cells of each side) and folds the results into a real
 *    {@link Cover} degree — the only place `soft`/`hard` get non-`none` values.
 */

import type { Space } from "./space";

/** Cover degree a sightline grants its target. */
export type Cover = "none" | "soft" | "hard";

/**
 * The result of a sightline query: whether the target is visible, and the cover
 * degree the line grants. The single-line {@link sight} reports `cover: "none"`
 * when clear (a single line is either clear or blocked — cover *degrees* are a
 * multi-line fold); {@link visibility}'s footprint fold is where `soft`/`hard`
 * arise.
 */
export type SightResult = {
  visible: boolean;
  cover: Cover;
}

/**
 * What blocks a sightline. An options object (not positional args) so a new
 * blocker slots in without a signature break.
 */
export type SightOptions<P> = {
  /**
   * Whether a cell blocks sight passing *through* it (terrain `blocksSight`, a
   * solid token). Defaults to "nothing blocks". Only the **intermediate** cells
   * of `line(a, b)` are consulted; a unit never blocks its own or its target's
   * cell.
   */
  blocksCell?: (p: P) => boolean;
  /**
   * Whether a thin wall sits on the edge between two **consecutive** cells of the
   * sightline spine. Called for every adjacent pair the line
   * crosses, *including* the pairs touching the endpoints — a wall flush against
   * the source or target still blocks the shot (unlike a solid cell, which is
   * exempt at the endpoints). Order-independent by the canonicalizer the
   * encounter layer builds it from; defaults to "no edge blocks".
   */
  blocksEdge?: (from: P, to: P) => boolean;
}

/**
 * Whether `a` can see `b`, center-to-center — the **single-line primitive**.
 * Walks the connected `space.line(a, b)` spine; the line is blocked if any
 * **intermediate** cell's `blocksCell` is true, OR any **consecutive pair**'s
 * `blocksEdge` is true. The two endpoints' *cells* are never consulted (standing
 * in or looking into a solid cell doesn't block the sightline), but the *edges*
 * touching them are (a wall flush against you still stops the shot).
 *
 * Returns `{ visible, cover }`. A single line is binary — clear (`cover: "none"`)
 * or blocked — so `sight` never reports a cover *degree* itself; that's
 * {@link visibility}'s footprint fold.
 */
/** Named arguments for {@link sight} — the grid, endpoints, and {@link SightOptions}. */
export type SightArgs<P> = {
  space: Space<P>;
  a: P;
  b: P;
} & SightOptions<P>

export function sight<P>(args: SightArgs<P>): SightResult {
  const { space, a, b, blocksCell, blocksEdge } = args;
  if (!blocksCell && !blocksEdge) return { visible: true, cover: "none" };

  const spine = space.line(a, b);
  for (let i = 1; i < spine.length; i++) {
    // The edge crossed stepping from spine[i-1] into spine[i]. Checked for every
    // pair, endpoints included (a wall flush against source/target still blocks).
    if (blocksEdge && blocksEdge(spine[i - 1]!, spine[i]!)) {
      return { visible: false, cover: "none" };
    }
    // The cell entered — only intermediate cells block (skip the last = target).
    if (blocksCell && i < spine.length - 1 && blocksCell(spine[i]!)) {
      return { visible: false, cover: "none" };
    }
  }
  return { visible: true, cover: "none" };
}

/**
 * What grants cover along a sightline, beyond fully blocking it.
 * Extends {@link SightOptions}: the same `blocksCell`/`blocksEdge` decide
 * visibility, and these decide the cover *degree* a partially-obstructed line
 * grants. All optional — absent ⇒ no cover is ever granted (only full blocking).
 *
 * RAW cover (core book p. 65): **soft** cover is "smoke, foliage, trees, low
 * walls" — visual interference that doesn't reliably block fire; **hard** cover
 * is "ruined buildings, tall walls" — solid enough to hide behind, but only if
 * the target is *adjacent* to it. We model that distinction as two predicates the
 * encounter layer derives from terrain flags.
 */
export type VisibilityOptions<P> = {
  /**
   * Whether a cell grants **soft** cover when a sightline passes through it
   * (terrain `cover: "soft"`, e.g. smoke/foliage). Consulted on intermediate
   * cells of each sampled line.
   */
  softCell?: (p: P) => boolean;
  /**
   * Whether a cell grants **hard** cover when a sightline passes through it
   * (terrain `cover: "hard"`, e.g. a building). Consulted on intermediate cells.
   * RAW: hard cover only applies if the *target* is adjacent to the cover, so
   * `visibility` downgrades hard→soft unless an adjacency check passes (see its
   * doc).
   */
  hardCell?: (p: P) => boolean;
  /**
   * Whether a thin wall on the edge between two **consecutive** cells of a
   * sampled line grants **soft** cover (terrain edge `cover: "soft"`, e.g. a low
   * wall — RAW p.66 names low walls as soft cover explicitly). Consulted for
   * every consecutive pair of `space.line(a, b)`, **including the pairs that
   * touch the line's endpoints** — this is the deliberate asymmetry with the
   * cell closures above: a solid *cell* at an endpoint is exempt (standing on
   * cover doesn't give you cover from your own cell), but an *edge* flush
   * against the attacker or the target is genuinely between them and still
   * counts. This mirrors {@link SightOptions.blocksEdge}'s identical endpoint
   * treatment — don't "fix" the asymmetry, it's load-bearing.
   */
  softEdge?: (from: P, to: P) => boolean;
  /**
   * Whether a thin wall on the edge between two **consecutive** cells of a
   * sampled line grants **hard** cover (terrain edge `cover: "hard"`, e.g. a
   * sturdier barricade). Same endpoint-inclusive treatment as {@link softEdge}.
   * RAW: hard cover only applies if the target is adjacent to the cover — for an
   * edge, "adjacent" means the edge lies on the *target footprint's boundary*
   * (an edge between a target cell and one of its neighbors), the edge-layer
   * twin of the cell-layer adjacency check below.
   */
  hardEdge?: (from: P, to: P) => boolean;
} & SightOptions<P>

/** A footprint as a set of occupied cells — `space.footprint(anchor, size).cells()`. */
export type Footprint<P> = readonly P[];

/**
 * Multi-line, footprint-folding line-of-sight + cover — the RAW "any part of
 * *your* space to any part of *theirs*" query. `from`/`to` are **footprints**
 * (each a set of cells; a Size-1 unit is just one cell), so this works unchanged
 * from a single hex up to a Size-3 mech. {@link sight} is the single-line primitive it folds over.
 *
 * The fold, faithful to the rulebook's cover rules (core book p. 65, "Checking
 * for cover"):
 *
 *  - **Visibility / flanking.** Run `sight` across the cross-product of the two
 *    footprints' cells. If *any* pair has a fully-clear line (no `blocksCell`/
 *    `blocksEdge`), the target is **visible** — and that clear line is exactly the
 *    rulebook's "flanking" line that *defeats* hard cover ("flanked if it is
 *    possible to draw a line totally clear of hard cover"). If *every* line is
 *    fully blocked, the target is not visible (`{ visible: false }`).
 *
 *  - **Cover degree** (only when visible). Look at the sampled lines that are
 *    *not* fully blocked:
 *      - **none** — at least one sampled line is fully *clear* of any cover cell
 *        too (a clean shot exists), i.e. the attacker is positioned with an
 *        unobstructed angle. RAW: "if a line can be drawn mostly unbroken… neither
 *        character has soft cover."
 *      - **hard** — every clear-of-blockers line that reaches the target is
 *        obstructed, the obstruction includes a **hard**-cover cell, AND the
 *        target footprint is **adjacent** to that hard cover (RAW: hard cover
 *        needs adjacency). Hard outranks soft (one cover type at a time, the
 *        stronger wins for the defender).
 *      - **soft** — obstructed by cover, but the hard-cover adjacency condition
 *        isn't met (a distant building grants only soft) or the obstruction is
 *        soft cover (smoke/foliage).
 *
 * **Documented simplifications:**
 *  - We sample **cell centers** (one line per footprint-cell pair), not sub-cell
 *    corner points. RAW's "draw a line from center to center" is center-based;
 *    the footprint cross-product already captures "any part to any part" at
 *    whole-cell resolution, which is the grid resolution Lancer plays at. Corner
 *    sub-sampling is a later fidelity bump, not needed for correct degrees.
 *  - The hard-cover **same-SIZE-or-smaller** clause (a Size-3 mech gets no hard
 *    cover from a Size-1 rock) is **not** enforced here — cover cells carry no
 *    size, and terrain isn't sized. Deferred.
 */
/** Named arguments for {@link visibility} — the grid, footprints, and {@link VisibilityOptions}. */
export type VisibilityArgs<P> = {
  space: Space<P>;
  from: Footprint<P>;
  to: Footprint<P>;
} & VisibilityOptions<P>

export function visibility<P>(args: VisibilityArgs<P>): SightResult {
  const { space, from, to, softCell, hardCell, softEdge, hardEdge, blocksCell, blocksEdge } = args;

  let anyVisible = false;
  let anyClear = false; // a visible line with no cover cell/edge at all
  let sawSoft = false;
  let sawHard = false;

  // Whether any target cell is adjacent to a hard-cover cell — the RAW gate that
  // separates hard cover from soft ("only benefit from hard cover if adjacent").
  // The edge arm mirrors this: a hard edge counts as adjacent when it lies on the
  // target footprint's boundary (an edge to one of the target's neighbors).
  const targetAdjacentToHard =
    (hardCell ? to.some((t) => space.neighbors(t).some((n) => hardCell(n))) : false) ||
    (hardEdge ? to.some((t) => space.neighbors(t).some((n) => hardEdge(t, n))) : false);

  for (const a of from) {
    for (const b of to) {
      const r = sight({ space, a, b, blocksCell, blocksEdge });
      if (!r.visible) continue;
      anyVisible = true;

      // Inspect the cover cells/edges along this (visible) line. `space.line` is
      // only walked here (a second time — `sight` already walked it for
      // blocking) when some cover closure is actually supplied, so the no-cover
      // call shape stays a single line-walk per pair.
      let lineSoft = false;
      let lineHard = false;
      if (softCell || hardCell || softEdge || hardEdge) {
        const spine = space.line(a, b);
        if (softCell || hardCell) {
          // Intermediate cells only — a unit never gets cover from its own or
          // its target's cell (see the cell-closure docs above).
          for (let i = 1; i < spine.length - 1; i++) {
            const c = spine[i]!;
            if (hardCell && hardCell(c)) lineHard = true;
            else if (softCell && softCell(c)) lineSoft = true;
          }
        }
        if (softEdge || hardEdge) {
          // Every consecutive pair, endpoints included — the deliberate
          // asymmetry with the cell loop above (see softEdge/hardEdge's docs).
          for (let i = 1; i < spine.length; i++) {
            const edgeFrom = spine[i - 1]!;
            const edgeTo = spine[i]!;
            if (hardEdge && hardEdge(edgeFrom, edgeTo)) lineHard = true;
            else if (softEdge && softEdge(edgeFrom, edgeTo)) lineSoft = true;
          }
        }
      }
      if (!lineSoft && !lineHard) anyClear = true;
      if (lineSoft) sawSoft = true;
      if (lineHard) sawHard = true;
    }
  }

  if (!anyVisible) return { visible: false, cover: "none" };

  // A fully-clear line beats everything: clean shot ⇒ no cover (and it's the
  // flanking line that defeats hard cover).
  if (anyClear) return { visible: true, cover: "none" };

  // Every visible line is obstructed by some cover. Hard outranks soft, but only
  // when the target is actually adjacent to the hard cover (RAW); otherwise the
  // hard obstruction degrades to soft.
  if (sawHard && targetAdjacentToHard) return { visible: true, cover: "hard" };
  if (sawHard || sawSoft) return { visible: true, cover: "soft" };
  return { visible: true, cover: "none" };
}
